import dayjs from 'dayjs';

import { DateRange } from 'context/DateProvider';

import {
  DATE_RANGES,
  DateRangeOptions,
  getMonthToYesterday,
  getWeekToYesterday,
  getYearToYesterday,
} from 'components/DateSelector/utils';

export const getAllTimeRange = (portfolioStartDate: Date, portfolioEndDate: Date) => ({
  from: portfolioStartDate,
  to: portfolioEndDate,
});

/**
 * The latest date portfolio measurements can be fetched for — yesterday. A portfolio's `end` is its
 * planned campaign end and is frequently in the future, so it must be clamped.
 */
export const getMeasurementEndDate = (portfolioEndDate?: Date | null): Date => {
  const yesterday = dayjs().subtract(1, 'day');
  if (!portfolioEndDate) return yesterday.toDate();
  // Cap at yesterday whenever the portfolio end is today or later — today has not
  // fully elapsed, so day-granularity comparison (not millisecond) is required.
  return dayjs(portfolioEndDate).isAfter(yesterday, 'day') ? yesterday.toDate() : new Date(portfolioEndDate);
};

const getCompletedQuarterPresets = (portfolioStartDate: Date, portfolioEndDate: Date): DateRangeOptions => {
  const quarters: DateRangeOptions = {};
  const pStart = dayjs(portfolioStartDate);
  const pEnd = dayjs(portfolioEndDate);
  const now = dayjs();

  const startYear = pStart.year();
  const startQ = Math.floor(pStart.month() / 3);

  for (let y = now.year(); y >= startYear; y--) {
    for (let q = 3; q >= (y === startYear ? startQ : 0); q--) {
      const qStart = dayjs(new Date(y, q * 3, 1)).startOf('day');
      const qEnd = dayjs(new Date(y, q * 3 + 2, 1)).endOf('month');

      // Only include fully completed quarters that fit within the portfolio
      if (qEnd.isAfter(now, 'day')) continue;
      if (qStart.isBefore(pStart, 'day') || qEnd.isAfter(pEnd, 'day')) continue;

      quarters[`q${q + 1}_${y}`] = {
        name: `Q${q + 1} ${y}`,
        isPast: true,
        group: 'quarters',
        getDateRange: () => ({
          from: qStart.toDate(),
          to: qEnd.endOf('day').toDate(),
        }),
      };
    }
  }

  return quarters;
};

const isDateRangeWithinPeriod = (dateRange: DateRange, startDate: Date, endDate: Date): boolean => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return (
    dayjs(dateRange.from).isBetween(start, end, 'day', '[]') && dayjs(dateRange.to).isBetween(start, end, 'day', '[]')
  );
};

export const getPortfolioDateRanges = (portfolioStartDate: Date, portfolioEndDate: Date) => {
  const portfolioDateRanges: DateRangeOptions = {};
  const updatedDateRanges = {
    ...DATE_RANGES,
    weekToDate: {
      name: 'Week to yesterday',
      isPast: true,
      group: 'recent',
      getDateRange: getWeekToYesterday,
    },
    monthToDate: {
      name: 'Month to yesterday',
      isPast: true,
      group: 'recent',
      getDateRange: getMonthToYesterday,
    },
    yearToDate: {
      name: 'Year to yesterday',
      isPast: true,
      group: 'recent',
      getDateRange: getYearToYesterday,
    },
  };

  portfolioDateRanges.allTime = {
    name: 'All time',
    isPast: true,
    getDateRange: () => getAllTimeRange(portfolioStartDate, portfolioEndDate),
  };

  // Add "Last year" option if we're in a year when last year exists
  // (i.e., we're past Jan 1 of the current year)
  const lastYearStart = dayjs().subtract(1, 'year').startOf('year');
  const lastYearEnd = dayjs().subtract(1, 'year').endOf('year');

  if (dayjs().year() > lastYearStart.year()) {
    // @ts-expect-error
    updatedDateRanges.previousYear = {
      name: 'Previous year',
      isPast: true,
      group: 'previous',
      getDateRange: () => {
        const portfolioStart = dayjs(portfolioStartDate);
        const portfolioEnd = dayjs(portfolioEndDate);
        return {
          from: (portfolioStart.isAfter(lastYearStart) ? portfolioStart : lastYearStart).toDate(),
          to: (portfolioEnd.isBefore(lastYearEnd) ? portfolioEnd : lastYearEnd).toDate(),
        };
      },
    };
  }

  Object.entries(updatedDateRanges).forEach(([key, value]) => {
    if (isDateRangeWithinPeriod(value.getDateRange(), portfolioStartDate, portfolioEndDate)) {
      portfolioDateRanges[key] = value;
    }
  });

  // Add completed financial quarters that fit within the portfolio
  Object.assign(portfolioDateRanges, getCompletedQuarterPresets(portfolioStartDate, portfolioEndDate));

  return portfolioDateRanges;
};

export const getPortfolioDateRangesForMeasurements = (portfolioStartDate: Date, portfolioEndDate: Date) => {
  // For measurements, cap the end date at yesterday if it's after today
  const measurementEndDate = getMeasurementEndDate(portfolioEndDate);

  const candidateRanges: DateRangeOptions = {
    previousWeek: DATE_RANGES.previousWeek,
    previous30Days: DATE_RANGES.previous30Days,
    previous90Days: DATE_RANGES.previous90Days,
  };

  // Add "Previous year" as a candidate if we're past Jan 1 of the current year
  const lastYearStart = dayjs().subtract(1, 'year').startOf('year');
  const lastYearEnd = dayjs().subtract(1, 'year').endOf('year');

  if (dayjs().year() > lastYearStart.year()) {
    candidateRanges.previousYear = {
      name: 'Previous year',
      isPast: true,
      group: 'previous',
      getDateRange: () => {
        const portfolioStart = dayjs(portfolioStartDate);
        const measurementEnd = dayjs(measurementEndDate);
        return {
          from: (portfolioStart.isAfter(lastYearStart) ? portfolioStart : lastYearStart).toDate(),
          to: (measurementEnd.isBefore(lastYearEnd) ? measurementEnd : lastYearEnd).toDate(),
        };
      },
    };
  }

  // Filter candidates: only include presets whose range fits within the portfolio measurement period
  const portfolioMeasurementDateRanges: DateRangeOptions = {
    allTime: {
      name: 'All time',
      isPast: true,
      getDateRange: () => getAllTimeRange(portfolioStartDate, measurementEndDate),
    },
  };

  Object.entries(candidateRanges).forEach(([key, value]) => {
    if (isDateRangeWithinPeriod(value.getDateRange(), portfolioStartDate, measurementEndDate)) {
      portfolioMeasurementDateRanges[key] = value;
    }
  });

  return portfolioMeasurementDateRanges;
};

export const getCompareDateRanges = (start: Date, end: Date) => {
  // Convert dates to UTC to avoid DST issues
  const utcStart = dayjs(start).utc();
  const utcEnd = dayjs(end).utc();

  // Calculate difference in days
  const daysDifference = utcEnd.diff(utcStart, 'day', true);

  return {
    previousPeriod: {
      name: 'Previous period',
      isPast: true,
      getDateRange: () => ({
        from: dayjs(utcStart)
          .subtract(daysDifference + 1, 'day')
          .toDate(),
        to: dayjs(utcStart).subtract(1, 'd').toDate(),
      }),
    },
    previousMonth: {
      name: 'Previous month',
      isPast: true,
      getDateRange: () => ({
        from: dayjs(utcStart).subtract(1, 'month').toDate(),
        to: dayjs(utcEnd).subtract(1, 'month').toDate(),
      }),
    },
    previousYear: {
      name: 'Previous year',
      isPast: true,
      getDateRange: () => ({
        from: dayjs(utcStart).subtract(1, 'year').toDate(),
        to: dayjs(utcEnd).subtract(1, 'year').toDate(),
      }),
    },
  };
};
